import {
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  Clock,
  type LucideIcon,
  RefreshCw,
} from 'lucide-react'
import * as React from 'react'
import { cn } from '../../lib/cn'
import { Button } from '../atoms/Button'
import { Icon } from '../atoms/Icon'

/**
 * FreshnessBanner — surfaces the staleness of cached / materialized data.
 *
 * Built for the Phase-1 Report Builder where every report sources from
 * Materialized Views that admins refresh manually. Without an obvious
 * "Updated 3 hours ago — Refresh now" banner, the dashboard silently rots
 * and admins act on stale data.
 *
 * Four states map to the backend's Freshness.staleness_level enum:
 *   - fresh  (<24h):  green, low-emphasis, often hidden in detail view
 *   - warn   (24–72h): yellow, prominent banner with "Refresh now" CTA
 *   - stale  (>72h):  red, very prominent banner
 *   - cold:           neutral, "No data yet" empty-state messaging
 *
 * Domain shape (props mirror dp-admin-console reports.types.Freshness, but kept
 * decomposed so this primitive doesn't depend on app-level types):
 *
 *   <FreshnessBanner
 *     stalenessLevel={freshness.staleness_level}
 *     lastRefreshedAt={freshness.last_refreshed_at}
 *     sourceMVs={freshness.source_mvs}
 *     onRefresh={() => refreshAll()}
 *     refreshing={pending}
 *   />
 */

export type FreshnessLevel = 'fresh' | 'warn' | 'stale' | 'cold'

export interface FreshnessBannerProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'children'> {
  /** The staleness bucket — drives color, icon, default copy. */
  stalenessLevel: FreshnessLevel
  /** ISO 8601 string OR Date OR null. Drives the relative-time display. */
  lastRefreshedAt?: string | Date | null
  /** MV names underlying the report — surfaced in supporting text. */
  sourceMVs?: string[]
  /**
   * Override the relative-time computation. Useful in stories + tests
   * where `Date.now()` would yield non-deterministic output.
   */
  nowFn?: () => Date
  /** Click handler for the "Refresh now" button. Button hidden when omitted. */
  onRefresh?: () => void | Promise<void>
  /** Disables the refresh button + shows spinner. */
  refreshing?: boolean
  /**
   * compact = single-line footer ("Updated 12 min ago"); good below KPIs.
   * non-compact = full Alert-style banner; good above the report body.
   */
  compact?: boolean
  /** Override the default copy. Receives the computed relative-time label. */
  message?: React.ReactNode | ((relativeLabel: string) => React.ReactNode)
}

interface ToneStyle {
  container: string
  icon: LucideIcon
  iconColor: string
  buttonVariant: 'primary' | 'secondary' | 'ghost' | 'destructive'
}

const TONE: Record<FreshnessLevel, ToneStyle> = {
  fresh: {
    container: 'bg-ia-success-bg border-ia-success/20 text-ia-text',
    icon: CheckCircle2,
    iconColor: 'text-ia-success',
    buttonVariant: 'ghost',
  },
  warn: {
    container: 'bg-ia-warning-bg border-ia-warning/20 text-ia-text',
    icon: AlertTriangle,
    iconColor: 'text-ia-warning',
    buttonVariant: 'secondary',
  },
  stale: {
    container: 'bg-ia-error-bg border-ia-error/20 text-ia-text',
    icon: AlertCircle,
    iconColor: 'text-ia-error',
    buttonVariant: 'primary',
  },
  cold: {
    container: 'bg-ia-info-bg border-ia-info/20 text-ia-text',
    icon: Clock,
    iconColor: 'text-ia-info',
    buttonVariant: 'primary',
  },
}

const DEFAULT_MESSAGE: Record<FreshnessLevel, (rel: string) => string> = {
  fresh: (rel) => `Updated ${rel} ago.`,
  warn: (rel) => `Data is ${rel} old. Consider refreshing for the latest.`,
  stale: (rel) => `Data is ${rel} old — outdated. Refresh now for current values.`,
  cold: () => 'No data yet — refresh to populate this report.',
}

/**
 * formatRelative — humane duration string with smart unit-rollover and
 * a compact secondary unit when useful (e.g. "2 hours 59 min" instead
 * of "179 min"). Caller appends the right suffix ("ago" / "old").
 *
 * Units (rolling up):
 *   <1 min              → "<1 min"
 *   1–59 min            → "12 min" / "1 min"
 *   1h–<24h, with mins  → "2 hours 14 min" / "1 hour"
 *   24h–<7d, with hours → "3 days 4 hours" / "1 day"
 *   7d–<30d             → "2 weeks" / "1 week"
 *   30d–<365d           → "5 months" / "1 month"
 *   ≥365d               → "2 years" / "1 year"
 *
 * null/undefined → "—". Sub-minute futures collapse to "<1 min".
 *
 * Why custom (not date-fns / Intl.RelativeTimeFormat)?
 *   • Zero new dependency on the DS package
 *   • The "primary + secondary" granularity ("2 hours 14 min") is not
 *     what RelativeTimeFormat produces (it rounds to a single unit)
 *   • Centralized behavior so audit, users, freshness, and builder
 *     panes all read identically.
 */
export function formatRelative(
  when: string | Date | null | undefined,
  now: Date = new Date(),
): string {
  if (!when) return '—'
  const then = typeof when === 'string' ? new Date(when) : when
  const ms = now.getTime() - then.getTime()
  if (Number.isNaN(ms)) return '—'
  if (ms < 0) return '<1 min'
  const sec = Math.floor(ms / 1000)
  if (sec < 60) return '<1 min'
  const min = Math.floor(sec / 60)
  if (min < 60) return `${min} min`
  const hr = Math.floor(min / 60)
  if (hr < 24) {
    const restMin = min - hr * 60
    const head = `${hr} ${hr === 1 ? 'hour' : 'hours'}`
    return restMin > 0 ? `${head} ${restMin} min` : head
  }
  const days = Math.floor(hr / 24)
  if (days < 7) {
    const restHr = hr - days * 24
    const head = `${days} ${days === 1 ? 'day' : 'days'}`
    return restHr > 0 ? `${head} ${restHr} ${restHr === 1 ? 'hour' : 'hours'}` : head
  }
  if (days < 30) {
    const weeks = Math.floor(days / 7)
    return `${weeks} ${weeks === 1 ? 'week' : 'weeks'}`
  }
  if (days < 365) {
    const months = Math.floor(days / 30)
    return `${months} ${months === 1 ? 'month' : 'months'}`
  }
  const years = Math.floor(days / 365)
  return `${years} ${years === 1 ? 'year' : 'years'}`
}

export const FreshnessBanner = React.forwardRef<HTMLDivElement, FreshnessBannerProps>(
  (
    {
      className,
      stalenessLevel,
      lastRefreshedAt,
      sourceMVs,
      nowFn,
      onRefresh,
      refreshing = false,
      compact = false,
      message,
      ...props
    },
    ref,
  ) => {
    const tone = TONE[stalenessLevel]
    const now = nowFn ? nowFn() : new Date()
    const relative = formatRelative(lastRefreshedAt, now)
    const computedMessage =
      typeof message === 'function'
        ? message(relative)
        : (message ?? DEFAULT_MESSAGE[stalenessLevel](relative))

    const sourcesLabel =
      sourceMVs && sourceMVs.length > 0
        ? sourceMVs.length === 1
          ? `Source: ${sourceMVs[0]}`
          : `Sources: ${sourceMVs.length} MVs`
        : null

    if (compact) {
      // Compact = single-line footer style. No background tint, no CTA button —
      // just relative-time + optional source count.
      return (
        <div
          ref={ref}
          role="status"
          className={cn('flex items-center gap-2 text-caption text-ia-text-tertiary', className)}
          {...props}
        >
          <Icon icon={tone.icon} size="xs" className={tone.iconColor} aria-hidden="true" />
          <span>{computedMessage}</span>
          {sourcesLabel && (
            <>
              <span aria-hidden="true">·</span>
              <span title={sourceMVs?.join(', ')}>{sourcesLabel}</span>
            </>
          )}
        </div>
      )
    }

    return (
      <div
        ref={ref}
        role="status"
        aria-live="polite"
        className={cn(
          'w-full rounded-md border px-4 py-3 flex gap-3 items-center',
          tone.container,
          className,
        )}
        {...props}
      >
        <div className={cn('flex-shrink-0', tone.iconColor)}>
          <Icon icon={tone.icon} size="md" aria-hidden="true" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-body-sm font-medium">{computedMessage}</div>
          {sourcesLabel && (
            <div
              className="text-caption text-ia-text-secondary mt-0.5"
              title={sourceMVs?.join(', ')}
            >
              {sourcesLabel}
            </div>
          )}
        </div>
        {onRefresh && (
          <Button
            variant={tone.buttonVariant}
            size="sm"
            onClick={onRefresh}
            loading={refreshing}
            leftIcon={RefreshCw}
            className="shrink-0"
          >
            Refresh now
          </Button>
        )}
      </div>
    )
  },
)
FreshnessBanner.displayName = 'FreshnessBanner'
